import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { AuthenticationService } from "src/app/core/services/authentication.service";
import { UsersService } from "src/app/routes/users/users.service";

export const ONBOARDING_STORAGE_KEY = "onboarding.completed";

interface OnboardingStep {
  title: string;
  description: string;
  actionLabel?: string;
  actionRoute?: string[];
}

const ADMINISTRATOR_STEPS: OnboardingStep[] = [
  {
    title: "Welcome to SoliMarket",
    description:
      "Let's walk through the basics of setting up your program. This will only take a minute.",
  },
  {
    title: "Create a Market",
    description:
      "Markets are the programs your farms and participants operate under.",
    actionLabel: "Go to Markets",
    actionRoute: ["/markets/new"],
  },
  {
    title: "Add a Farm",
    description: "Register the farms that will accept benefits.",
    actionLabel: "Go to Farms",
    actionRoute: ["/stores/new"],
  },
  {
    title: "Assign Benefits",
    description:
      "Connect an issuance partner to start issuing benefits to participants.",
    actionLabel: "Go to Issuance Partners",
    actionRoute: ["/issuing-entities/new"],
  },
  {
    title: "You're all set",
    description:
      "Finish to start managing your program. You can revisit these pages anytime from the sidebar.",
  },
];

const AGENCY_USER_STEPS: OnboardingStep[] = [
  {
    title: "Welcome to SoliMarket",
    description:
      "Let's walk through the basics of your portal. This will only take a minute.",
  },
  {
    title: "Review Participants",
    description: "View and manage the participants enrolled in your program.",
    actionLabel: "Go to Participants",
    actionRoute: ["/participants"],
  },
  {
    title: "Check Inventory",
    description: "Track issued cards and stock levels.",
    actionLabel: "Go to Inventory",
    actionRoute: ["/inventory"],
  },
  {
    title: "Run Reports",
    description: "Access utilization and sales reports for your program.",
    actionLabel: "Go to Reports",
    actionRoute: ["/reports"],
  },
  {
    title: "You're all set",
    description:
      "Finish to start using your portal. You can revisit these pages anytime from the sidebar.",
  },
];

@Component({
  selector: "app-onboarding-wizard",
  templateUrl: "./onboarding-wizard.component.html",
  styleUrls: ["./onboarding-wizard.component.scss"],
})
export class OnboardingWizardComponent implements OnInit {
  steps: OnboardingStep[] = [];
  currentStep = 0;

  constructor(
    public activeModal: NgbActiveModal,
    private router: Router,
    private authenticationService: AuthenticationService,
    private usersService: UsersService
  ) {}

  ngOnInit(): void {
    const user = this.authenticationService.currentUser();
    this.steps = user?.isInRole("Administrator")
      ? ADMINISTRATOR_STEPS
      : AGENCY_USER_STEPS;
  }

  get step(): OnboardingStep {
    return this.steps[this.currentStep];
  }

  get isLastStep(): boolean {
    return this.currentStep === this.steps.length - 1;
  }

  next(): void {
    if (!this.isLastStep) {
      this.currentStep++;
    }
  }

  back(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  goTo(route: string[]): void {
    this.complete();
    this.router.navigate(route);
  }

  complete(): void {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, "true");
    this.activeModal.close();

    const userId = this.authenticationService.currentUser()?.id;
    if (userId) {
      this.usersService.completeOnboarding(userId).subscribe();
    }
  }
}
